import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Share2, Eye } from "lucide-react";

/** Per-document sharing popover — toggle which connected orgs can open it. */
export default function DocShareControl({ sid, doc, onChanged }) {
    const [open, setOpen] = useState(false);
    const [orgs, setOrgs] = useState(null);
    const [log, setLog] = useState([]);
    const shared = doc.shared_orgs || [];

    const load = async () => {
        try {
            const [c, l] = await Promise.all([
                api.get("/care-access/mine"),
                api.get(`/documents/access-log/${sid}`),
            ]);
            setOrgs((c.data.connections || []).filter(x => x.status === "active"));
            setLog((l.data.log || []).filter(x => x.doc_id === doc.id).slice(0, 3));
        } catch { setOrgs([]); }
    };

    const toggle = async (orgId, on) => {
        try {
            await api.put(`/documents/share/${sid}/${doc.id}`, { org_id: orgId, shared: on });
            toast.success(on ? "Shared — they can now open this document." : "Sharing removed.");
            onChanged && onChanged();
        } catch { toast.error("Couldn't update sharing."); }
    };

    return (
        <div className="relative">
            <button type="button" title={shared.length ? `Shared with ${shared.length} org(s)` : "Share with your care team"}
                onClick={() => { setOpen(o => !o); if (!orgs) load(); }}
                data-testid={`doc-share-btn-${doc.id}`}
                className={`p-2 rounded-full transition-colors ${shared.length ? "text-teal-dark bg-teal-soft" : "text-[#6E7A74] hover:bg-cream-dark"}`}>
                <Share2 size={14} />
            </button>
            {open && (
                <div className="absolute right-0 top-9 z-30 w-72 bg-white border border-cream-dark rounded-2xl shadow-xl p-4" data-testid={`doc-share-popover-${doc.id}`}>
                    <p className="text-xs font-semibold text-[#1F2624]">Who can open this document?</p>
                    {orgs === null ? (
                        <p className="mt-2 text-xs text-[#6E7A74]">Loading…</p>
                    ) : orgs.length === 0 ? (
                        <p className="mt-2 text-xs text-[#4C5551]">No organizations connected yet — connect one from Settings first.</p>
                    ) : (
                        <div className="mt-2 space-y-1.5">
                            {orgs.map(o => {
                                const on = shared.includes(o.org_id);
                                return (
                                    <button key={o.org_id} type="button" onClick={() => toggle(o.org_id, !on)}
                                        data-testid={`doc-share-toggle-${doc.id}-${o.org_id}`}
                                        className="w-full flex items-center justify-between gap-2 text-left">
                                        <span className="text-xs text-[#1F2624] truncate">{o.org_name || "Organization"}</span>
                                        <span className={`w-9 h-5 rounded-full relative shrink-0 transition-colors ${on ? "bg-teal" : "bg-cream-dark"}`}>
                                            <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${on ? "left-[18px]" : "left-0.5"}`} />
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                    {log.length > 0 && (
                        <div className="mt-3 border-t border-cream-dark pt-2">
                            <p className="text-[10px] font-semibold uppercase tracking-wide text-[#6E7A74] flex items-center gap-1"><Eye size={10} /> Recently viewed</p>
                            {log.map((l, i) => (
                                <p key={i} className="text-[11px] text-[#4C5551] mt-0.5">{l.org_name || l.viewer} · {new Date(l.at).toLocaleDateString()}</p>
                            ))}
                        </div>
                    )}
                    <p className="mt-2 text-[10px] text-[#6E7A74]">Only documents you switch on are ever visible. Every view is logged.</p>
                </div>
            )}
        </div>
    );
}
